import { Button } from "./ui/button";
import { PageHero } from "./PageHero";
import { Card, CardContent } from "./ui/card";
import { Lightbulb, CalendarDays, ArrowRight } from "lucide-react";
import { PageLink } from "./PageLink";
import { caseStudies } from "../data/caseStudies";
import { Mark } from './Mark';

interface ResourcesPageProps {
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// Still empty — real entries get added as they exist. We don't fabricate content.
const sections = [
  {
    icon: Lightbulb,
    title: "Insights",
    blurb: "Notes on agentic automation, RAG, and enterprise AI delivery.",
  },
  {
    icon: CalendarDays,
    title: "Events",
    blurb: "Webinars, talks, and where to meet the TurfAI team.",
  },
];

export function ResourcesPage({ onNavigate, onNavigateToDemo }: ResourcesPageProps) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>Learn from the <Mark>TurfAI team</Mark></>}
        lede="Case studies, insights, and events — practical material on shipping agentic automation in the enterprise."
      />


      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">Case studies</span>
          <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">
            TurfAI <Mark>in production</Mark>
          </h2>
          <p className="mt-4 text-[19px] text-soft-black max-w-3xl">
            How teams in eight sectors run on TurfAI. Every story is anonymous.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {caseStudies.map((c) => (
              <PageLink
                key={c.slug}
                page={c.pageId}
                onNavigate={onNavigate}
                className="group flex flex-col rounded-lg border border-softblack-tint-20 hover:border-royal-indigo transition-colors p-6 text-left"
              >
                <span className="text-xs font-bold uppercase tracking-wider text-soft-black">{c.sector}</span>
                <h3 className="mt-2 text-[19px] font-semibold text-dark-indigo group-hover:text-royal-indigo transition-colors">{c.title}</h3>
                <p className="mt-3 font-display text-[28px] font-bold leading-tight text-dark-indigo">{c.highlights[0].metric}</p>
                <p className="text-[13px] text-soft-black flex-1">{c.highlights[0].label}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-royal-indigo">
                  Read the story <ArrowRight className="w-4 h-4" />
                </span>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {sections.map((s) => (
              <Card key={s.title} className="p-6 bg-white border-softblack-tint-20">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-indigo-tint-20 rounded-xl flex items-center justify-center mb-4">
                    <s.icon className="w-6 h-6 text-royal-indigo" />
                  </div>
                  <h2 className="text-[18px] lg:text-[20px] font-semibold text-dark-indigo mb-2">{s.title}</h2>
                  <p className="text-sm text-soft-black mb-4">{s.blurb}</p>
                  <p className="text-sm font-medium text-royal-indigo">Nothing published yet.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight mb-6">See TurfAI on <Mark>your workflow</Mark></h2>
          <p className="text-[22px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">We'll walk you through it in 30 minutes, on a process you run today.</p>
          <Button variant="onDark" size="lg" className="px-8 h-12" onClick={onNavigateToDemo}>Schedule a demo</Button>
        </div>
      </section>
    </div>
  );
}
